/**
 * Real Apple and Google Play brand marks.
 *
 * The generic outline icons (Tabler's IconBrandApple / IconBrandGooglePlay) read as grey
 * decoration — people don't recognise them as "this is on the App Store". These are the
 * actual logos: Apple's glyph, and Play's four-colour triangle. They are what a user is
 * used to seeing on a download button, so they carry the meaning on their own.
 *
 * Apple's mark is monochrome by design and must follow the surrounding text colour, so it
 * works on both light and dark surfaces. Play's is always its four brand colours.
 */

export interface PlatformLogoProps {
  size?: number;
  /** Apple's glyph only. Defaults to the inherited text colour. */
  color?: string;
}

/** The Apple logo. Monochrome — inherits `color` so it works on light and dark. */
export function AppleLogo({ size = 24, color = 'currentColor' }: PlatformLogoProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={color}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Apple"
    >
      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
    </svg>
  );
}

/**
 * The Google Play logo — the four-colour triangle, not a grey outline.
 *
 * Four paths: the blue spine on the left, then green (top), amber (the right tip) and red
 * (bottom) radiating from the fold.
 */
export function GooglePlayLogo({ size = 24 }: PlatformLogoProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Google Play"
    >
      {/* Blue — the spine down the left edge */}
      <path
        fill="#00A0FF"
        d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5Z"
      />
      {/* Green — the top face */}
      <path fill="#00E676" d="M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
      {/* Amber — the tip */}
      <path
        fill="#FFCE00"
        d="M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81Z"
      />
      {/* Red — the bottom face */}
      <path fill="#FF3A44" d="M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12Z" />
    </svg>
  );
}
